import { Config, Jetty, Mobru, NonWorkingTime, Product, Quay, TerminalPlannerBerth } from 'src/types'

export function msToHours(milliseconds: number) {
  return Math.floor(milliseconds / (1000 * 60 * 60))
}

export function hoursToMs(hours: number) {
  return hours * 1000 * 60 * 60
}

export type TProductAtQuay = Product & {
  mobruName?: string
  jettyName?: string
  holderType: 'Jetty' | 'Cargo belts'
  quayName: string
}

// get all products at quays
export function getAllProductsAtQuays(quays: Quay[]): TProductAtQuay[] {
  if (!quays) return []
  const products: TProductAtQuay[] = quays
    .map(quay => {
      const jettyProducts: TProductAtQuay[] = []
      quay.jetties.forEach((jet: Jetty) => {
        jet.products.forEach((product: Product) => {
          jettyProducts.push({
            ...product,
            jettyName: jet.name,
            holderType: 'Jetty' as const,
            quayName: quay.name
          })
        })
      })

      const mobruProducts: TProductAtQuay[] = []
      quay.mobrus.forEach((mob: Mobru) => {
        mob.products.forEach((product: Product) => {
          mobruProducts.push({
            ...product,
            mobruName: mob.name,
            holderType: 'Cargo belts' as const,
            quayName: quay.name
          })
        })
      })

      return [...jettyProducts, ...mobruProducts]
    })
    .reduce((acc, val) => acc.concat(val), [])
  return products
}

// extract unique products from the config
export function extractUniqueProducts(products: TProductAtQuay[]) {
  // Step 1: Keep only the required fields
  const simplified = products.map(item => ({
    product: item.product,
    productGroup: item.productGroup,
    productUnit: item.productUnit,
    exportSequenceNumber: item.exportSequenceNumber
  }))

  // Step 2: Deduplicate based on the 4 keys
  const seen = new Set()
  const unique = simplified.filter(item => {
    const key = `${item.product}|${item.productGroup}|${item.productUnit}|${item.exportSequenceNumber}`
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })

  return unique
}

// create a map from unique products to be its product group as key and products as value
export function createProductGroupMap(
  products: Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[]
) {
  const productGroupMap = new Map<string, Set<string>>()
  products.forEach(item => {
    if (!productGroupMap.has(item.productGroup)) {
      productGroupMap.set(item.productGroup, new Set())
    }
    productGroupMap.get(item.productGroup)?.add(item.product)
  })

  const result: Record<
    string,
    Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[]
  > = {}
  productGroupMap.forEach((_, key) => {
    result[key] = products.filter(item => item.productGroup === key)
  })
  return result
}

// check if there is any product unit or export sequence number difference
export function checkProductUnitAndSequenceDifferences(
  products: Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[]
) {
  const groups: Map<
    string,
    {
      product: string
      productGroup: string
      units: Set<string | undefined>
      seqs: Set<number | undefined>
    }
  > = new Map()

  // Group by (product|productGroup)
  for (const item of products) {
    const key = `${item.product}|${item.productGroup}`
    if (!groups.has(key)) {
      groups.set(key, {
        product: item.product,
        productGroup: item.productGroup,
        units: new Set(),
        seqs: new Set()
      })
    }
    const g = groups.get(key)
    if (item.productUnit !== undefined) g.units.add(item.productUnit)
    if (item.exportSequenceNumber !== undefined) g.seqs.add(item.exportSequenceNumber)
  }

  // Build result only for groups with differences (size > 1)
  const result: {
    product: string
    productGroup: string
    productUnitDifference: string[]
    exportSequenceNumberDifference: number[]
  }[] = []
  for (const [, g] of groups) {
    const unitDiff = Array.from(g.units)
    const seqDiff = Array.from(g.seqs)

    if (unitDiff.length > 1 || seqDiff.length > 1) {
      // Sort to make output deterministic
      unitDiff.sort()
      seqDiff.sort((a, b) => a - b)

      result.push({
        product: g.product,
        productGroup: g.productGroup,
        productUnitDifference: unitDiff,
        exportSequenceNumberDifference: seqDiff
      })
    }
  }

  return result
}

export type ProductUnitInconsistency = {
  product: string
  productGroup: string
  units: string[]
}

export function getGlobalProductUnitInconsistencies(config: Config): ProductUnitInconsistency[] {
  const all = getAllProductsAtQuays(config?.quays || [])
  const unique = extractUniqueProducts(all)
  const diffs = checkProductUnitAndSequenceDifferences(unique)
  return diffs
    .filter(d => (d.productUnitDifference?.length || 0) > 1)
    .map(d => ({
      product: d.product,
      productGroup: d.productGroup,
      units: d.productUnitDifference
    }))
}

export function formatProductUnitInconsistencyMessages(diffs: ProductUnitInconsistency[]): string[] {
  return diffs.map(d => `Product ${d.product} (Group ${d.productGroup}) has multiple units: ${d.units.join(', ')}`)
}

export function isThereAnyProductExistWithDifferentUnit(
  allExistingProducts: Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[],
  newProducts: Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[]
) {
  return newProducts.some(newProduct =>
    allExistingProducts.some(
      existingProduct =>
        existingProduct.product === newProduct.product &&
        existingProduct.productGroup === newProduct.productGroup &&
        existingProduct.productUnit !== newProduct.productUnit
    )
  )
}

export function getAnyProductExistWithDifferentUnit(
  allExistingProducts: Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[],
  newProducts: Pick<Product, 'product' | 'productGroup' | 'productUnit' | 'exportSequenceNumber'>[]
) {
  return allExistingProducts.filter(existingProduct => {
    return (
      newProducts.filter(
        newProduct =>
          existingProduct.product === newProduct.product &&
          existingProduct.productGroup === newProduct.productGroup &&
          existingProduct.productUnit !== newProduct.productUnit
      ).length > 0
    )
  })
}

export function getBerthErrors(berth: TerminalPlannerBerth, quay: Quay) {
  const errors = []
  if (!berth.terminalName) {
    errors.push('Berth name is empty')
  }
  if (berth.startBollard === null) {
    errors.push('Bollard start is not set')
  }
  if (berth.endBollard === null) {
    errors.push('Bollard end is not set')
  }
  if (berth.startBollard > berth.endBollard) {
    errors.push('Bollard start must be before bollard end')
  }
  if (!berth.berthId) {
    errors.push('Berth location is not selected on the map')
  }
  quay.berths.forEach(b => {
    // Check that current birth startBollard and endBollard does not overlap with other berths
    if (b.berthId !== berth.berthId) {
      if (berth.startBollard > b.startBollard && berth.startBollard < b.endBollard) {
        errors.push('Bollard start overlaps with another berth')
      }
      if (berth.endBollard > b.startBollard && berth.endBollard < b.endBollard) {
        errors.push('Bollard end overlaps with another berth')
      }
    }
  })

  return errors
}

export function getMobruErrors(mobru: Mobru) {
  const errors = []
  if (!mobru.name) {
    errors.push('Mobru name is empty')
  }
  if (mobru.bollardStart === null) {
    errors.push('Bollard start is not set')
  }
  if (mobru.bollardStart < 0) {
    errors.push('Bollard start can not be negative')
  }
  if (mobru.bollardEnd === null) {
    errors.push('Bollard end is not set')
  }
  if (mobru.bollardEnd < 0) {
    errors.push('Bollard end can not be negative')
  }
  if (mobru.bollardDefaultStart === null) {
    errors.push('Default bollard start is not set')
  }
  if (mobru.bollardDefaultEnd === null) {
    errors.push('Default bollard end is not set')
  }
  if (mobru.bollardStart >= mobru.bollardEnd) {
    errors.push('Bollard start must be before bollard end')
  }
  if (mobru.bollardDefaultStart >= mobru.bollardDefaultEnd) {
    errors.push('Default bollard start must be before default bollard end')
  }
  if (mobru.color === null) {
    errors.push('Color is not set')
  }
  if (mobru.color && !mobru.color.startsWith('#')) {
    errors.push('Color is not valid')
  }
  if (!mobru.products || mobru.products.length === 0) {
    errors.push('At least one product must be added')
  }

  return errors
}

export function getJettyErrors(jetty: Jetty) {
  const errors = []
  if (!jetty.name) {
    errors.push('Jetty name is empty')
  }
  if (jetty.bollard === null) {
    errors.push('Bollard is not set')
  }
  if (jetty.bollard < 0) {
    errors.push('Bollard can not be negative')
  }
  if (jetty.bollardDefaultStart === null) {
    errors.push('Default bollard start is not set')
  }
  if (jetty.bollardDefaultEnd === null) {
    errors.push('Default bollard end is not set')
  }
  if (jetty.bollardDefaultStart > jetty.bollardDefaultEnd) {
    errors.push('Default bollard start must be before default bollard end')
  }
  if (jetty.color === null) {
    errors.push('Color is not set')
  }
  if (jetty.color && !jetty.color.startsWith('#')) {
    errors.push('Color is not valid')
  }
  if (!jetty.products || jetty.products.length === 0) {
    errors.push('At least one product must be added')
  }

  return errors
}

export function getProductErrors(product: Product) {
  const errors = []
  if (!product.product) {
    errors.push('Product name is empty')
  }
  if (!product.productGroup) {
    errors.push('Product group is empty')
  }
  if (product.loadSpeed === null) {
    errors.push('Load speed is not set')
  }
  if (product.loadSpeed < 0) {
    errors.push('Load speed can not be negative')
  }
  if (product.manning === null) {
    errors.push('Manning is not set')
  }
  if (product.manning < 0) {
    errors.push('Manning can not be negative')
  }
  if (product.color === null) {
    errors.push('Color is not set')
  }
  if (product.color && !product.color.startsWith('#')) {
    errors.push('Color is not valid')
  }
  if (product.slackTimes === null) {
    errors.push('Slack times is not set')
  }
  if (product.slackTimes) {
    if (product.slackTimes.ds === null) {
      errors.push('Slack times for deep sea is not set')
    }
    if (product.slackTimes.ss === null) {
      errors.push('Slack times for short sea is not set')
    }
    if (product.slackTimes.barge === null) {
      errors.push('Slack times for barge is not set')
    }
    if (product.slackTimes.ds) {
      if (product.slackTimes.ds.prior === null) {
        errors.push('Slack time prior for deep sea is not set')
      }
      if (product.slackTimes.ds.after === null) {
        errors.push('Slack time after for deep sea is not set')
      }
      if (product.slackTimes.ds.prior < 0) {
        errors.push('Slack time prior for deep sea can not be negative')
      }
      if (product.slackTimes.ds.after < 0) {
        errors.push('Slack time after for deep sea can not be negative')
      }
    }
    if (product.slackTimes.ss) {
      if (product.slackTimes.ss.prior === null) {
        errors.push('Slack time prior for short sea is not set')
      }
      if (product.slackTimes.ss.after === null) {
        errors.push('Slack time after for short sea is not set')
      }
      if (product.slackTimes.ss.prior < 0) {
        errors.push('Slack time prior for short sea can not be negative')
      }
      if (product.slackTimes.ss.after < 0) {
        errors.push('Slack time after for short sea can not be negative')
      }
    }
    if (product.slackTimes.barge) {
      if (product.slackTimes.barge.prior === null) {
        errors.push('Slack time prior for barge is not set')
      }
      if (product.slackTimes.barge.after === null) {
        errors.push('Slack time after for barge is not set')
      }
      if (product.slackTimes.barge.prior < 0) {
        errors.push('Slack time prior for barge can not be negative')
      }
      if (product.slackTimes.barge.after < 0) {
        errors.push('Slack time after for barge can not be negative')
      }
    }
  }

  return errors
}

export function getQuayErrors(quay: Quay) {
  const errors = []
  if (!quay.name) {
    errors.push('Quay name is empty')
  }
  if (quay.minBollard === null) {
    errors.push('Min bollard is not set')
  }
  if (quay.maxBollard === null) {
    errors.push('Max bollard is not set')
  }
  if (quay.minBollard > quay.maxBollard) {
    errors.push('Min bollard must be before max bollard')
  }
  if (quay.quayType === null) {
    errors.push('Quay type is not set')
  }
  if (quay.role === null) {
    errors.push('Quay role is not set')
  }

  return errors
}

export function getTerminalOperationsErrors(config: Config) {
  const errors = []
  if (config.bollardLength === null) {
    errors.push('Bollard length is not set')
  }
  if (config.bollardLength < 1) {
    errors.push('Bollard length must be at least 1')
  }
  if (config.etaTooLateThreshold === null) {
    errors.push('Eta too late threshold is not set')
  }
  if (config.etaTooLateThreshold < 0) {
    errors.push('Eta too late threshold can not be negative')
  }

  return errors
}

export function getNonWorkingHourErrors(nonWorkingHour: NonWorkingTime) {
  const errors = []
  if (nonWorkingHour.hour === null) {
    errors.push('Hour is not set')
  }
  if (nonWorkingHour.minutes === null) {
    errors.push('Minutes is not set')
  }
  if (nonWorkingHour.duration === null) {
    errors.push('Duration is not set')
  }
  if (nonWorkingHour.duration < 0) {
    errors.push('Duration can not be negative')
  }
  if (nonWorkingHour.startDay === null) {
    errors.push('Start day is not set')
  }

  return errors
}
